<!-- Ported from the Polaris kit: src/product/components/SettingRow.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09):
     - No `row.locked` and no Upgrade badge (X-5 / D-S4: a feature behind a flag that is off is not shown, never shown
       locked). `row.disabled` stays, for a control that depends on something else.
     - No `row.logo`: BrandLogo (src/product/brands) is not ported; no area asked for it.
     - The readonly copy uses the app's copyTextToClipboard (its timer cleared on unmount); "Copy", "Copied" and
       "{label} copied" go through i18n.
     - `row.disabled` also reaches the readonly (Copy) and destructive (field and button) kinds. -->
<script setup>
// One control of a settings section, by kind. Every kind is a registry Field with its label, help and error in
// the same places, so forty pages read the same way. `disabled` is for a control that depends on something else
// (its help says on what). Kinds: switch, checkbox, radio, readonly (with Copy), action, destructive (type to
// confirm), note, select, and text (the default; `row.type` for email, number...).
//   row: { id, kind, label, help?, disabled?, options? ([{ value, label }] or strings), value? (readonly),
//          actionLabel? (action, destructive), match? (destructive: the words to type), placeholder?, type? }
import { computed, onBeforeUnmount, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldTitle,
} from '../ui/field';
import { Input } from '../ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '../ui/input-group';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Switch } from '../ui/switch';
import OptionSelect from './OptionSelect.vue';

const props = defineProps({
  row: { type: Object, required: true },
  modelValue: { type: null, default: undefined },
  error: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue', 'action']);
const { t } = useI18n();
const uid = useId();
const id = computed(() => `${uid}-${props.row.id}`);
const describedBy = computed(() => {
  if (props.error) return `${id.value}-error`;
  return props.row.help ? `${id.value}-help` : undefined;
});
const set = value => emit('update:modelValue', value);
const selectOptions = computed(() =>
  (props.row.options ?? []).map(option => ({
    value: option.value ?? option,
    label: option.label ?? option,
  }))
);
const copied = ref(false);
let copiedTimer = null;
async function copy() {
  await copyTextToClipboard(String(props.row.value ?? ''));
  copied.value = true;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copied.value = false;
  }, 2000);
}
onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <!-- switch and checkbox: control first, words to its right -->
  <Field
    v-if="row.kind === 'switch' || row.kind === 'checkbox'"
    orientation="horizontal"
    class="items-start"
    :data-disabled="row.disabled || undefined"
  >
    <Switch
      v-if="row.kind === 'switch'"
      :id="id"
      class="mt-0.5"
      :model-value="!!modelValue"
      :disabled="row.disabled"
      :aria-describedby="describedBy"
      @update:model-value="set"
    />
    <Checkbox
      v-else
      :id="id"
      class="mt-0.5"
      :model-value="!!modelValue"
      :disabled="row.disabled"
      :aria-describedby="describedBy"
      @update:model-value="set"
    />
    <FieldContent>
      <FieldLabel :for="id" class="font-normal">{{ row.label }}</FieldLabel>
      <FieldDescription
        v-if="row.help"
        :id="`${id}-help`"
        class="type-polaris-caption"
      >
        {{ row.help }}
      </FieldDescription>
    </FieldContent>
  </Field>

  <Field v-else-if="row.kind === 'radio'">
    <FieldTitle :id="`${id}-label`">{{ row.label }}</FieldTitle>
    <RadioGroup
      :model-value="modelValue"
      :aria-labelledby="`${id}-label`"
      :disabled="row.disabled"
      @update:model-value="set"
    >
      <div
        v-for="option in selectOptions"
        :key="option.value"
        class="flex items-center gap-2"
      >
        <RadioGroupItem
          :id="`${id}-${option.value}`"
          :value="option.value"
        /><FieldLabel :for="`${id}-${option.value}`" class="font-normal">
          {{ option.label }}
        </FieldLabel>
      </div>
    </RadioGroup>
    <FieldDescription v-if="row.help" class="type-polaris-caption">
      {{ row.help }}
    </FieldDescription>
  </Field>

  <Field v-else-if="row.kind === 'readonly'">
    <FieldLabel :for="id">{{ row.label }}</FieldLabel>
    <InputGroup>
      <InputGroupInput
        :id="id"
        readonly
        :model-value="row.value"
        class="text-polaris-muted-foreground"
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton :disabled="row.disabled" @click="copy">
          {{
            copied
              ? t('POLARIS.SETTING_ROW.COPIED')
              : t('POLARIS.SETTING_ROW.COPY')
          }}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
    <span class="sr-only" aria-live="polite">
      {{
        copied
          ? t('POLARIS.SETTING_ROW.COPIED_LABEL', { label: row.label })
          : ''
      }}
    </span>
  </Field>

  <Field
    v-else-if="row.kind === 'action'"
    orientation="horizontal"
    class="items-center justify-between"
  >
    <FieldContent>
      <FieldTitle>{{ row.label }}</FieldTitle>
      <FieldDescription v-if="row.help" class="type-polaris-caption">
        {{ row.help }}
      </FieldDescription>
    </FieldContent>
    <Button
      variant="outline"
      size="sm"
      :disabled="row.disabled"
      @click="emit('action', row)"
    >
      {{ row.actionLabel }}
    </Button>
  </Field>

  <Field
    v-else-if="row.kind === 'destructive'"
    :data-invalid="!!error || undefined"
  >
    <FieldLabel :for="id">{{ row.label }}</FieldLabel>
    <div class="flex flex-wrap gap-2">
      <Input
        :id="id"
        class="min-w-0 flex-1"
        :model-value="modelValue"
        :disabled="row.disabled"
        autocomplete="off"
        :aria-describedby="describedBy"
        @update:model-value="set"
      />
      <Button
        variant="destructive"
        :disabled="row.disabled || !row.match || modelValue !== row.match"
        @click="emit('action', row)"
      >
        {{ row.actionLabel }}
      </Button>
    </div>
    <FieldDescription
      v-if="row.help"
      :id="`${id}-help`"
      class="type-polaris-caption"
    >
      {{ row.help }}
    </FieldDescription>
  </Field>

  <p
    v-else-if="row.kind === 'note'"
    class="type-polaris-body text-polaris-muted-foreground"
  >
    {{ row.label }}
  </p>

  <!-- text and select -->
  <Field v-else :data-invalid="!!error || undefined">
    <FieldLabel :for="id">{{ row.label }}</FieldLabel>
    <OptionSelect
      v-if="row.kind === 'select'"
      :id="id"
      :model-value="modelValue"
      :disabled="row.disabled"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
      :options="selectOptions"
      @update:model-value="set"
    />
    <Input
      v-else
      :id="id"
      :type="row.type || 'text'"
      :model-value="modelValue"
      :placeholder="row.placeholder"
      :disabled="row.disabled"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
      @update:model-value="set"
    />
    <FieldError v-if="error" :id="`${id}-error`">{{ error }}</FieldError>
    <FieldDescription
      v-else-if="row.help"
      :id="`${id}-help`"
      class="type-polaris-caption"
    >
      {{ row.help }}
    </FieldDescription>
  </Field>
</template>
